import { DocumentTypeEnum } from 'api-hooks/order/travel-document';
import { PlusCircleSVG } from 'common/assets';
import { EnumList } from 'common/constants/enum';
import DeleteIconButton from 'components/common/delete-icon-button';
import { Button } from 'components/elements/buttons';
import { FormState, useFormState } from 'components/elements/form';
import Text from 'components/elements/text';
import EnumSelectInput from 'legacy-modules/master/select-input/enum';
import {
  CardSection,
  FullContainer,
  HalfContainer,
  SeparatorHorizontal,
  SeparatorVertical,
} from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import {
  DocumentPurchaseFormType,
  DocumentPurchaseItemFormType,
} from './form-type';
import AddOnsTable from './tables/addons-table';
import ApecTable from './tables/apec-table';
import InsuranceTable from './tables/insurance-table';
import PassportTable from './tables/passport-table';
import VisaDetailTable from './tables/visa-detail-table';
import VisaTable from './tables/visa-table';
import {
  addNewDocumentPurchase,
  addNewDocumentPurchaseGuest,
} from '../hooks/use-get-default-values';

interface Props {
  parentIndex: number;
  onRemove: () => void;
  fieldLength?: number;
}

function Field(props: Props) {
  const { t } = useTranslation();
  const { getValues, setValue, trigger } = useFormContext();
  const { editable } = useFormState();
  const documentPurchase = getValues(
    `documentPurchases.${props.parentIndex}`,
  ) as DocumentPurchaseItemFormType;
  const guestName = `documentPurchases.${props.parentIndex}.documentPurchaseGuests`;

  const { documentType } = documentPurchase;

  const onChangeDocumentType = React.useCallback(() => {
    const documentPurchaseGuests = [addNewDocumentPurchaseGuest()];
    if (editable) {
      setValue(
        `documentPurchases.${props.parentIndex}.documentPurchaseGuests`,
        documentPurchaseGuests,
      );
      trigger('documentPurchases');
    }
  }, [editable, props.parentIndex, setValue, trigger]);

  // const onAfterSelect = React.useCallback(
  //   (value: VisaDocumentLiteModel[]) => {
  //     const visaDocumentOptions: Option[] = [];
  //     value.forEach((vd) => {
  //       vd.visaDocumentItems.forEach((vdi) => {
  //         const option = {
  //           id: vdi.preference.id,
  //           label: [vd.code, vd.name, vdi.preference.name].join(' - '),
  //           preferenceId: vdi.preference.id,
  //           price: vdi.price,
  //           visaDocumentItemId: vdi.id,
  //         };
  //         visaDocumentOptions.push(option);
  //       });
  //     });
  //     setValue('visaDocumentOptions', visaDocumentOptions);
  //   },
  //   [setValue],
  // );

  const render = React.useCallback(() => {
    switch (documentType) {
      case DocumentTypeEnum.visa:
        return (
          <>
            <VisaTable parentIndex={props.parentIndex} />
            <SeparatorVertical $margin={24} />

            {getValues(guestName)?.map((guest, guestIndex) => {
              // if (!guest.details.length) return null;
              return (
                <React.Fragment key={guest.customId}>
                  <Text variant="LabelMedium">{guest?.guestName}</Text>
                  <SeparatorVertical $margin={24} />
                  {guest.guestName && (
                    <VisaDetailTable
                      parentIndex={props.parentIndex}
                      guestIndex={guestIndex}
                    />
                  )}
                  <SeparatorVertical $margin={24} />
                </React.Fragment>
              );
            })}
            <SeparatorVertical $margin={24} />
            <AddOnsTable parentIndex={props.parentIndex} />
          </>
        );
      case DocumentTypeEnum.insurance:
        return (
          <>
            <InsuranceTable parentIndex={props.parentIndex} />
            <SeparatorVertical $margin={24} />
            <AddOnsTable parentIndex={props.parentIndex} />
          </>
        );
      case DocumentTypeEnum.passport:
        return (
          <>
            <PassportTable parentIndex={props.parentIndex} />
            <SeparatorVertical $margin={24} />
            <AddOnsTable parentIndex={props.parentIndex} />
          </>
        );
      case DocumentTypeEnum.apec_card:
        return (
          <>
            <ApecTable parentIndex={props.parentIndex} />
            <SeparatorVertical $margin={24} />
            <AddOnsTable parentIndex={props.parentIndex} />
          </>
        );
      default:
        return null;
    }
  }, [documentType, props.parentIndex, getValues, guestName]);

  return (
    <CardSection key={documentPurchase.customId}>
      <FullContainer>
        <FullContainer
          $style={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <Text variant="HeadingXSmall">
            {t('document:document_details')} {props.parentIndex + 1}
          </Text>
          <FormState>
            {({ editable }) =>
              (props.fieldLength || 0) > 1 &&
              editable && (
                <>
                  <SeparatorHorizontal $margin={16} />
                  <DeleteIconButton
                    onClick={props.onRemove}
                    rootStyle={{ height: '32px' }}
                  />
                </>
              )
            }
          </FormState>
        </FullContainer>
        <SeparatorVertical $margin={24} />
        <HalfContainer>
          <EnumSelectInput
            enumType={EnumList.DocumentType}
            name={`documentPurchases.${props.parentIndex}.documentType`}
            label={t('document:document_type')}
            placeholder={t('document:document_type')}
            required
            onAfterChange={onChangeDocumentType}
            clearable={false}
          />
        </HalfContainer>
        <SeparatorVertical $margin={16} />
        {/* {documentType === DocumentTypeEnum.visa && (
          <>
            <HalfContainer>
              <VisaDocumentMultiInput
                name={`documentPurchases.${props.parentIndex}.visaDocuments`}
                onAfterSelect={onAfterSelect}
              />
            </HalfContainer>
            <SeparatorVertical $margin={16} />
          </>
        )} */}
        {render()}
      </FullContainer>
    </CardSection>
  );
}

export default function DocumentDetailField() {
  const { t } = useTranslation();
  const { control, getValues } = useFormContext<DocumentPurchaseFormType>();
  const { append, remove } = useFieldArray({
    control,
    name: 'documentPurchases',
    keyName: '__customId',
  });

  const documentPurchases = getValues('documentPurchases');

  const onAddDocument = React.useCallback(() => {
    append(addNewDocumentPurchase());
  }, [append]);

  const onRemoveDocument = React.useCallback(
    (index) => remove(index),
    [remove],
  );
  const { editable } = useFormState();

  return (
    <>
      {documentPurchases.map((documentPurchase, index) => (
        <React.Fragment key={documentPurchase.customId}>
          <Field
            parentIndex={index}
            onRemove={() => onRemoveDocument(index)}
            fieldLength={documentPurchases.length}
          />
          <SeparatorVertical $margin={24} />
        </React.Fragment>
      ))}

      {editable && (
        <>
          <SeparatorHorizontal $margin={16} />
          <Button
            startEnhancer={(color, size) => (
              <PlusCircleSVG color={color} size={size} />
            )}
            buttonType="secondary"
            onClick={onAddDocument}
          >
            {t('common:add')}
          </Button>
        </>
      )}
      <SeparatorVertical $margin={24} />
    </>
  );
}
